/**
 * Tiny global toast store. No deps — one event, one subscriber.
 * Used to surface optimistic-update failures ("Couldn't move task — version conflict").
 */

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'

export type ToastKind = 'info' | 'success' | 'error'
export interface Toast {
  id: string
  kind: ToastKind
  message: string
  ttl?: number
  /** Optional call to action rendered as a button next to the message. */
  action?: ToastAction
}

export interface ToastAction {
  label: string
  onSelect: () => void
}

type Listener = (toasts: Toast[]) => void

let toasts: Toast[] = []
const listeners = new Set<Listener>()

function emit() {
  for (const l of listeners) l(toasts)
}

/** Every toast vanishes after 5 seconds, with or without an action. */
export const TOAST_TTL_MS = 5000

/**
 * The running timer of each toast. Hovering or focusing a toast pauses its
 * timer; leaving it resumes with the time that was left.
 */
const timers = new Map<
  string,
  { handle: ReturnType<typeof setTimeout> | null; remaining: number; startedAt: number }
>()

function startTimer(id: string, ms: number) {
  timers.set(id, { handle: setTimeout(() => dismiss(id), ms), remaining: ms, startedAt: Date.now() })
}

/** Pauses a toast's timer while the pointer or focus is on it. */
export function pauseToast(id: string) {
  const timer = timers.get(id)
  if (!timer?.handle) return
  clearTimeout(timer.handle)
  timers.set(id, {
    handle: null,
    remaining: Math.max(0, timer.remaining - (Date.now() - timer.startedAt)),
    startedAt: 0,
  })
}

/** Resumes a paused toast with the time it had left. */
export function resumeToast(id: string) {
  const timer = timers.get(id)
  if (!timer || timer.handle) return
  startTimer(id, timer.remaining)
}

export function toast(message: string, opts: { kind?: ToastKind; ttl?: number; action?: ToastAction } = {}) {
  const id = crypto.randomUUID()
  const ttl = opts.ttl ?? TOAST_TTL_MS
  const t: Toast = { id, message, kind: opts.kind ?? 'info', ttl, action: opts.action }
  toasts = [...toasts, t]
  emit()
  if (t.ttl && t.ttl > 0) startTimer(id, t.ttl)
}

export function dismiss(id: string) {
  const timer = timers.get(id)
  if (timer?.handle) clearTimeout(timer.handle)
  timers.delete(id)
  toasts = toasts.filter((x) => x.id !== id)
  emit()
}

export function ToastHost() {
  const [list, setList] = useState<Toast[]>(toasts)
  useEffect(() => {
    const l: Listener = (t) => setList(t)
    listeners.add(l)
    return () => {
      listeners.delete(l)
    }
  }, [])
  return (
    // Toasts stack one above the other, newest at the bottom, and never
    // overlap. On mobile the stack sits above the bottom navigation.
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-20 right-4 z-50 flex w-[min(28rem,calc(100vw-2rem))] flex-col items-end gap-2 md:bottom-4"
    >
      <AnimatePresence initial={false} mode="popLayout">
        {list.map((t) => (
          <motion.div
            key={t.id}
            layout
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            onClick={() => dismiss(t.id)}
            onMouseEnter={() => pauseToast(t.id)}
            onMouseLeave={() => resumeToast(t.id)}
            onFocus={() => pauseToast(t.id)}
            onBlur={() => resumeToast(t.id)}
            role="status"
            className={cn(
              'pointer-events-auto flex w-full cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm shadow-lg backdrop-blur',
              t.kind === 'error' && 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-200',
              t.kind === 'success' &&
                'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200',
              t.kind === 'info' &&
                'border-surface-200 dark:border-surface-700 dark:bg-surface-800 text-surface-900 dark:text-surface-50 bg-white',
            )}
          >
            <span className="min-w-0 flex-1 truncate">{t.message}</span>
            {t.action && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  dismiss(t.id)
                  t.action!.onSelect()
                }}
                className="shrink-0 rounded px-2 py-0.5 text-xs font-semibold underline-offset-2 hover:underline"
              >
                {t.action.label}
              </button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
